import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type { PromoCode, UpdatePromoCodePayload } from '../types/promoCode';
import { promoCodeQueryKeys } from './usePromoCodes';

export interface UpdatePromoCodeVariables {
  id: string;
  payload: UpdatePromoCodePayload;
}

export function useUpdatePromoCode(
  options?: UseMutationOptions<PromoCode, Error, UpdatePromoCodeVariables>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['promoCode', 'update'],
    mutationFn: async ({ id, payload }: UpdatePromoCodeVariables) => {
      return service.updatePromoCode(id, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: promoCodeQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: promoCodeQueryKeys.detail(variables.id),
      });
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
